import React, { useCallback, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Modal } from '../../common/Modal';
import { useLanguage } from '../../../features/language/useLanguage';
import { inventoryService } from '../../../services/inventoryService';
import { PickGreenBeansStep } from './PickGreenBeansStep';
import { UploadStep } from './UploadStep';
import { ReviewStep } from './ReviewStep';
import { ResultStep } from './ResultStep';
import { Stepper, type StepKey } from './Stepper';
import type { GreenBeanPickerItem, ImportRow, RoastType } from './types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ImportRoastedBulkModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [step, setStep] = useState<StepKey>('pick');
  const [selectedGBs, setSelectedGBs] = useState<GreenBeanPickerItem[]>([]);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [createdCount, setCreatedCount] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setStep('pick');
    setSelectedGBs([]);
    setRows([]);
    setCreatedCount(0);
    setSubmitError(null);
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handlePicked = (gbs: GreenBeanPickerItem[]) => {
    setSelectedGBs(gbs);
    setStep('upload');
  };

  const handleParsed = (parsed: ImportRow[]) => {
    setRows(parsed);
    setStep('review');
  };

  const handleSubmit = async (toSend: ImportRow[]) => {
    setSubmitError(null);
    try {
      const payload = toSend.map((r) => ({
        source_green_bean_item_id: r.sourceGreenBeanItemId,
        initial_qty: r.initialQty,
        standard_cost: r.standardCost,
        roast_type: r.roastType as RoastType,
      }));
      const resp = await inventoryService.importRoastedBulks(payload);
      setCreatedCount(resp.created);
      setStep('result');
      onSuccess();
    } catch (e) {
      setSubmitError(t('inventory.importRoastedBulk.errors.serverError'));
      // Swallow the throw so React doesn't see an unhandled rejection — the
      // user stays on Review and can fix + resubmit.
      console.error('RB import failed:', e);
    }
  };

  const stepLabels: Record<StepKey, string> = {
    pick: t('inventory.importRoastedBulk.step1Title'),
    upload: t('inventory.importRoastedBulk.step2Title'),
    review: t('inventory.importRoastedBulk.step3Title'),
    result: t('inventory.importRoastedBulk.step4Title'),
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('inventory.importRoastedBulk.modalTitle')}
      maxWidth="6xl"
    >
      <div className="space-y-5">
        <div className="px-1">
          <Stepper current={step} labels={stepLabels} />
        </div>

        {submitError && (
          <div
            data-test-id="import-rb-submit-error"
            className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{submitError}</span>
          </div>
        )}

        <div key={step} className="relative">
          {step === 'pick' && <PickGreenBeansStep onContinue={handlePicked} />}
          {step === 'upload' && (
            <UploadStep
              selectedGBs={selectedGBs}
              onParsed={handleParsed}
              onBack={() => setStep('pick')}
            />
          )}
          {step === 'review' && (
            <ReviewStep
              initialRows={rows}
              onBack={() => setStep('upload')}
              onSubmit={handleSubmit}
            />
          )}
          {step === 'result' && <ResultStep count={createdCount} onDone={handleClose} />}
        </div>
      </div>
    </Modal>
  );
};
